import { Box, Button, Snackbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getExplanationFeedbackApi,
  submitExplanationFeedbackApi,
} from '../../api/learners';

interface ExplanationHelpfulFeedbackProps {
  questionId: string;
  sessionId: string;
}

const feedbackButtonSx = {
  minWidth: 0,
  px: 1.5,
  py: 0.625,
  borderRadius: 100,
  bgcolor: '#fff',
  border: '1px solid #e2e8f0',
  color: '#475569',
  fontSize: 13,
  fontWeight: 600,
  textTransform: 'none' as const,
  lineHeight: 1.4,
  gap: 0.5,
  boxShadow: 'none',
  transform: 'translateY(0)',
  transition:
    'transform 0.2s ease, background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    bgcolor: '#f8fafc',
    borderColor: '#cbd5e1',
    boxShadow: 'none',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    transform: 'none',
  },
};

export const ExplanationHelpfulFeedback = ({
  questionId,
  sessionId,
}: ExplanationHelpfulFeedbackProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setSelected(null);
    setSubmitting(false);

    if (!questionId) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    getExplanationFeedbackApi(questionId)
      .then((feedback) => {
        if (!active) return;
        const latest = feedback?.[0];
        setSelected(
          latest && typeof latest.helpful === 'boolean' ? latest.helpful : null
        );
      })
      .catch((err) => {
        if (!active) return;
        console.error('Failed to fetch explanation feedback', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [questionId, sessionId]);

  const handleSelect = async (helpful: boolean) => {
    if (loading || submitting || !sessionId || !questionId) return;
    const previous = selected;
    setSelected(helpful);
    try {
      setSubmitting(true);
      await submitExplanationFeedbackApi(sessionId, questionId, { helpful });
      setToast(
        helpful
          ? t('PAL_AR_EXPLANATION_FEEDBACK_YES_TOAST')
          : t('PAL_AR_EXPLANATION_FEEDBACK_NO_TOAST')
      );
    } catch (err) {
      console.error('Failed to submit explanation feedback', err);
      setSelected(previous);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: '#64748b',
            lineHeight: 1.4,
          }}
        >
          {t('PAL_AR_EXPLANATION_HELPFUL')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button
            onClick={() => handleSelect(true)}
            disabled={loading || submitting}
            disableElevation
            sx={{
              ...feedbackButtonSx,
              ...(selected === true && {
                bgcolor: '#f0fdf4',
                borderColor: '#86efac',
                color: '#15803d',
              }),
            }}
          >
            <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>
              👍
            </Box>
            {t('PAL_AR_YES')}
          </Button>
          <Button
            onClick={() => handleSelect(false)}
            disabled={loading || submitting}
            disableElevation
            sx={{
              ...feedbackButtonSx,
              ...(selected === false && {
                bgcolor: '#fef2f2',
                borderColor: '#fecaca',
                color: '#dc2626',
              }),
            }}
          >
            <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>
              👎
            </Box>
            {t('PAL_AR_NO')}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={toast !== null}
        autoHideDuration={3200}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          sx={{
            px: '20px',
            py: '12px',
            borderRadius: '999px',
            bgcolor: '#1e293b',
            color: '#f1f5f9',
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.3)',
          }}
        >
          {toast}
        </Box>
      </Snackbar>
    </>
  );
};
